'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getComplaintHistory, type Building } from '@repo/api';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ComplaintHistorySection } from './ComplaintHistorySection';
import { ComplaintHistorySectionSkeleton } from './ComplaintHistorySectionSkeleton';

interface ComplaintHistoryProps {
  building: Building;
  buildingAddress: string;
  showSkeleton: boolean;
  isReportReady: boolean;
}

function ComplaintHistory({
  building,
  buildingAddress,
  showSkeleton,
  isReportReady,
}: ComplaintHistoryProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [loading, setLoading] = useState(true);
  const [complaintData, setComplaintData] = useState<any | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_complaints_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  useEffect(() => {
    if (!isReportReady) {
      setLoading(false);
      setComplaintData(null);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const response = await getComplaintHistory({ buildingId: building.id });
        setComplaintData(response);
      } catch (err) {
        console.error('[report][complaint-history] failed to load', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isReportReady]);

  const shouldShowSkeleton = showSkeleton || loading;
  if (shouldShowSkeleton) return <ComplaintHistorySectionSkeleton />;

  return (
    <div ref={sectionRef}>
      <ComplaintHistorySection
        buildingAddress={buildingAddress}
        isBlurRequired={!isReportReady}
        complaintHistoryData={complaintData}
      />
    </div>
  );
}

export default ComplaintHistory;
